import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Talent } from '../data/talentData'
import { generateRandomTalents, SCHOOLS, TRAINING_OPTIONS } from '../data/talentData'
import { getRandomHistoricalTalent, convertToGameTalent, HISTORICAL_TALENTS, type HistoricalTalent } from '../data/historicalTalents'

export type GameView = 'map' | 'internal' | 'talent' | 'city-management' | 'city'

export interface Building {
  id: string
  name: string
  level: number
  maxLevel: number
  output: number
}

export interface GameState {
  // 游戏状态
  currentView: GameView
  isPaused: boolean
  gameSpeed: number
  gameDate: { year: number; month: number }
  lastTick: number

  // 资源
  resources: {
    food: number
    money: number
    population: number
    prestige: number
  }

  // 资源产量（每月）
  resourceIncome: {
    food: number
    money: number
    population: number
  }

  // 建筑
  buildings: Building[]

  // 人口分配
  populationAllocation: {
    farming: number
    commerce: number
    construction: number
  }

  // 人才系统
  talents: Talent[]                    // 已招募的人才
  availableTalents: Talent[]           // 可招募的人才（人才市场）
  recruitCooldown: number              // 招募冷却（月）

  // 操作
  setView: (view: GameView) => void
  togglePause: () => void
  setGameSpeed: (speed: number) => void
  tick: (deltaTime: number) => void
  updateResources: (resources: Partial<GameState['resources']>) => void
  upgradeBuilding: (buildingId: string) => boolean
  setPopulationAllocation: (allocation: Partial<GameState['populationAllocation']>) => void

  // 人才操作
  recruitTalent: (talentId: string) => boolean
  dismissTalent: (talentId: string) => void
  appointTalent: (talentId: string, position: string | null) => void
  refreshAvailableTalents: () => void
  startTraining: (talentId: string, trainingId: string) => boolean
  completeTraining: (talentId: string) => void

  // 历史人物
  getHistoricalTalent: (talentId: string) => HistoricalTalent | undefined

  // 存档操作
  restoreGameState: (state: Partial<GameState>) => void
}

// 计算资源收入
const calculateIncome = (buildings: Building[], allocation: GameState['populationAllocation']): GameState['resourceIncome'] => {
  const farm = buildings.find(b => b.id === 'farm')
  const market = buildings.find(b => b.id === 'market')
  const house = buildings.find(b => b.id === 'house')

  return {
    food: (farm?.output || 0) * (1 + allocation.farming * 0.01),
    money: (market?.output || 0) * (1 + allocation.commerce * 0.01),
    population: (house?.output || 0) * 0.1,
  }
}

// 计算人才加成
const calculateTalentBonus = (talents: Talent[]) => {
  let foodBonus = 0
  let moneyBonus = 0
  let prestigeBonus = 0

  talents.forEach(talent => {
    if (!talent.position) return

    const school = SCHOOLS.find(s => s.id === talent.school)
    if (!school) return

    // 根据流派和职位提供加成
    switch (school.id) {
      case 'farmer':
        foodBonus += talent.politics * 0.001
        break
      case 'merchant':
        moneyBonus += talent.intelligence * 0.001
        break
      case 'confucian':
        prestigeBonus += talent.charisma * 0.01
        break
    }
  })

  return { foodBonus, moneyBonus, prestigeBonus }
}

// 定义需要持久化的状态类型
interface PersistedState {
  gameDate: { year: number; month: number }
  resources: {
    food: number
    money: number
    population: number
    prestige: number
  }
  resourceIncome: {
    food: number
    money: number
    population: number
  }
  buildings: Building[]
  populationAllocation: {
    farming: number
    commerce: number
    construction: number
  }
  talents: Talent[]
  recruitCooldown: number
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // 初始状态
      currentView: 'city',
      isPaused: true,
      gameSpeed: 1,
      gameDate: { year: -770, month: 1 }, // 公元前770年，春秋开始
      lastTick: 0,

      resources: {
        food: 1000,
        money: 500,
        population: 100,
        prestige: 10,
      },

      resourceIncome: {
        food: 50,
        money: 30,
        population: 5,
      },

      buildings: [
        { id: 'farm', name: '农田', level: 1, maxLevel: 5, output: 50 },
        { id: 'market', name: '市集', level: 1, maxLevel: 5, output: 30 },
        { id: 'house', name: '民居', level: 1, maxLevel: 5, output: 20 },
        { id: 'academy', name: '学宫', level: 0, maxLevel: 3, output: 0 },
      ],

      populationAllocation: {
        farming: 40,
        commerce: 30,
        construction: 20,
      },

      // 人才初始状态
      talents: [],
      availableTalents: generateRandomTalents(3),
      recruitCooldown: 0,

  // 操作方法
  setView: (view) => set({ currentView: view }),

  togglePause: () => set((state) => ({ isPaused: !state.isPaused })),

  setGameSpeed: (speed) => set({ gameSpeed: speed }),

  // 时间推进（核心逻辑）
  tick: (_deltaTime: number) => {
    const state = get()
    if (state.isPaused) return

    const now = Date.now()
    const timeSinceLastTick = now - state.lastTick

    // 基础时间单位：1个月 = 3000ms / gameSpeed
    const monthDuration = 3000 / state.gameSpeed

    if (timeSinceLastTick >= monthDuration) {
      set((state) => {
        // 推进时间
        let { year, month } = state.gameDate
        month++
        if (month > 12) {
          month = 1
          year++
        }

        // 计算资源收入
        const baseIncome = calculateIncome(state.buildings, state.populationAllocation)
        const talentBonus = calculateTalentBonus(state.talents)

        // 更新资源
        const newResources = {
          food: state.resources.food + baseIncome.food * (1 + talentBonus.foodBonus),
          money: state.resources.money + baseIncome.money * (1 + talentBonus.moneyBonus),
          population: state.resources.population + baseIncome.population,
          prestige: state.resources.prestige + talentBonus.prestigeBonus,
        }

        // 更新招募冷却
        const newCooldown = Math.max(0, state.recruitCooldown - 1)

        // 检查进修完成
        const updatedTalents = state.talents.map(talent => {
          if (talent.isTraining && talent.trainingEndTime && talent.trainingEndTime <= Date.now()) {
            return { ...talent, isTraining: false, trainingEndTime: undefined }
          }
          return talent
        })

        return {
          gameDate: { year, month },
          lastTick: now,
          resources: newResources,
          resourceIncome: baseIncome,
          recruitCooldown: newCooldown,
          talents: updatedTalents,
        }
      })
    }
  },

  updateResources: (newResources) => set((state) => ({
    resources: { ...state.resources, ...newResources }
  })),

  // 升级建筑
  upgradeBuilding: (buildingId: string) => {
    const state = get()
    const building = state.buildings.find(b => b.id === buildingId)

    if (!building || building.level >= building.maxLevel) return false

    const cost = building.level * 100
    if (state.resources.money < cost) return false

    set((state) => ({
      resources: {
        ...state.resources,
        money: state.resources.money - cost,
      },
      buildings: state.buildings.map(b =>
        b.id === buildingId
          ? { ...b, level: b.level + 1, output: b.output + 20 }
          : b
      ),
    }))

    return true
  },

  // 设置人口分配
  setPopulationAllocation: (allocation) => set((state) => {
    const newAllocation = { ...state.populationAllocation, ...allocation }
    const total = newAllocation.farming + newAllocation.commerce + newAllocation.construction

    // 确保总和不超过100
    if (total > 100) return state

    return { populationAllocation: newAllocation }
  }),

  // 招募人才
  recruitTalent: (talentId: string) => {
    const state = get()
    const talent = state.availableTalents.find(t => t.id === talentId)

    if (!talent) return false
    if (state.recruitCooldown > 0) return false

    // 招募成本
    const cost = {
      money: 100,
      prestige: 5,
    }

    if (state.resources.money < cost.money || state.resources.prestige < cost.prestige) {
      return false
    }

    set((state) => ({
      resources: {
        ...state.resources,
        money: state.resources.money - cost.money,
        prestige: state.resources.prestige - cost.prestige,
      },
      talents: [...state.talents, { ...talent, loyalty: 70 }],
      availableTalents: state.availableTalents.filter(t => t.id !== talentId),
      recruitCooldown: 3, // 3个月冷却
    }))

    return true
  },

  // 解雇人才
  dismissTalent: (talentId: string) => {
    set((state) => ({
      talents: state.talents.filter(t => t.id !== talentId),
    }))
  },

  // 任命人才
  appointTalent: (talentId: string, position: string | null) => {
    set((state) => ({
      talents: state.talents.map(t =>
        t.id === talentId ? { ...t, position } : t
      ),
    }))
  },

  // 刷新可招募人才
  refreshAvailableTalents: () => {
    const state = get()
    if (state.recruitCooldown > 0) return

    const currentYear = state.gameDate.year
    const newTalents: Talent[] = []

    // 尝试生成3个人才，有一定概率出现历史人物
    for (let i = 0; i < 3; i++) {
      const historical = getRandomHistoricalTalent(currentYear, 0.25) // 25%概率出现历史人物
      if (historical) {
        newTalents.push(convertToGameTalent(historical))
      } else {
        // 生成随机人才
        const randomTalents = generateRandomTalents(1)
        newTalents.push(randomTalents[0])
      }
    }

    set(() => ({
      availableTalents: newTalents,
      recruitCooldown: 1,
    }))
  },

  // 开始进修
  startTraining: (talentId: string, trainingId: string) => {
    const state = get()
    const talent = state.talents.find(t => t.id === talentId)
    const training = TRAINING_OPTIONS.find(t => t.id === trainingId)

    if (!talent || !training) return false
    if (talent.isTraining) return false
    if (state.resources.money < training.cost.money || state.resources.prestige < training.cost.prestige) {
      return false
    }

    const endTime = Date.now() + training.duration * 1000 // 简化：秒代替月

    set((state) => ({
      resources: {
        ...state.resources,
        money: state.resources.money - training.cost.money,
        prestige: state.resources.prestige - training.cost.prestige,
      },
      talents: state.talents.map(t =>
        t.id === talentId
          ? { ...t, isTraining: true, trainingEndTime: endTime }
          : t
      ),
    }))

    return true
  },

  // 完成进修（应用效果）
  completeTraining: (talentId: string) => {
    const state = get()
    const talent = state.talents.find(t => t.id === talentId)

    if (!talent || !talent.isTraining) return

    // 找到对应的进修项目（简化处理，实际应该记录选择的进修项目）
    // 这里随机应用一个效果作为示例
    const statToBoost = ['politics', 'military', 'intelligence', 'charisma'][Math.floor(Math.random() * 4)] as keyof Talent

    set((state) => ({
      talents: state.talents.map(t => {
        if (t.id !== talentId) return t

        const updates: Partial<Talent> = {
          isTraining: false,
          trainingEndTime: undefined,
          experience: t.experience + 100,
        }

        // 提升对应属性
        if (statToBoost === 'politics') updates.politics = Math.min(100, t.politics + 5)
        if (statToBoost === 'military') updates.military = Math.min(100, t.military + 5)
        if (statToBoost === 'intelligence') updates.intelligence = Math.min(100, t.intelligence + 5)
        if (statToBoost === 'charisma') updates.charisma = Math.min(100, t.charisma + 5)

        return { ...t, ...updates }
      }),
    }))
  },

  // 获取历史人物详情
  getHistoricalTalent: (talentId: string) => {
    const state = get()
    const talent = state.talents.find(t => t.id === talentId)
    if (!talent || !talent.isHistorical || !talent.historicalId) return undefined
    
    return HISTORICAL_TALENTS.find(h => h.id === talent.historicalId)
  },

  // 恢复游戏状态（用于读取存档）
  restoreGameState: (savedState) => {
    set((state) => ({
      ...state,
      ...savedState,
      // 确保这些字段存在
      currentView: savedState.currentView || 'map',
      isPaused: savedState.isPaused ?? true,
      gameSpeed: savedState.gameSpeed || 1,
      gameDate: savedState.gameDate || { year: -770, month: 1 },
      resources: savedState.resources || state.resources,
      resourceIncome: savedState.resourceIncome || state.resourceIncome,
      buildings: savedState.buildings || state.buildings,
      populationAllocation: savedState.populationAllocation || state.populationAllocation,
      talents: savedState.talents || state.talents,
      availableTalents: savedState.availableTalents || state.availableTalents,
      recruitCooldown: savedState.recruitCooldown ?? state.recruitCooldown,
      lastTick: Date.now(),
    }))
  },
}),
    {
      name: 'huaxia-chronicles-game-storage',
      partialize: (state): PersistedState => ({
        gameDate: state.gameDate,
        resources: state.resources,
        resourceIncome: state.resourceIncome,
        buildings: state.buildings,
        populationAllocation: state.populationAllocation,
        talents: state.talents,
        recruitCooldown: state.recruitCooldown,
      }),
    }
  )
)
